import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import NumbersIcon from '@mui/icons-material/Numbers';
import { FormControl, InputLabel, OutlinedInput } from '@mui/material';
import IMask from 'imask';
import React from 'react';
import { useCommonFormUiTranslation } from '../hooks';
import { buildEventTab, buildGeneralTab, buildHiddenTab, buildStyleTab, buildValidateTab, showWidgetEditDialog } from '../WidgetEditDialog';
import { FormHelperErrorDescription, FormTooltip } from '../WidgetForm/Components';
import { defaultWidgetLabelProvider, getLabelAndValuePairs, getVisibleError, handleWidgetUpdate, isSkipValidate, joinLabels, makeNewConfigUnique, renderForm, validateCustom, validateUniqueInList, } from '../WidgetForm/Utils';
import { getFormLabelSuffix } from '../WidgetForm/Utils/getFormLabelSuffix';
import { createIMaskInput } from '../WidgetFormText/Utils/createIMaskInput';
import { type } from './types';
const numberIntegerInputComponent = createIMaskInput({
    mask: IMask.MaskedNumber,
    radix: '.',
    mapToRadix: [','],
    scale: 0,
    signed: 'true',
});
const WidgetFormNumber = (props) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const value = (_a = props.internalData.value) !== null && _a !== void 0 ? _a : props.getDataNumberOrUndefined();
    const error = getVisibleError(props);
    const readOnly = props.config.readOnly || ((_b = props.rootParams.formParams) === null || _b === void 0 ? void 0 : _b.readOnly);
    const formLabelSuffix = getFormLabelSuffix(props);
    const elementLabel = formLabelSuffix ? props.config.label + formLabelSuffix : props.config.label;
    const fieldIsInteger = (_c = props.config.integer) !== null && _c !== void 0 ? _c : true;
    const numberFloatInputComponent = React.useMemo(() => {
        var _a;
        return createIMaskInput({
            mask: IMask.MaskedNumber,
            radix: '.',
            mapToRadix: [','],
            scale: (_a = props.config.numberOfFractionalDigits) !== null && _a !== void 0 ? _a : 14,
            signed: 'true',
            normalizeZeros: false,
            padFractionalZeros: true,
        });
    }, [props.config.numberOfFractionalDigits]);
    const ariaDescribedBy = React.useId();
    return (React.createElement(FormControl, { fullWidth: true, required: (_d = props.config.validate) === null || _d === void 0 ? void 0 : _d.required, error: !!error, disabled: props.config.disabled },
        React.createElement(InputLabel, { htmlFor: props.id, variant: 'outlined', required: (_e = props.config.validate) === null || _e === void 0 ? void 0 : _e.required, error: !!error, disabled: props.config.disabled }, elementLabel),
        React.createElement(FormTooltip, { tooltip: props.config.tooltip },
            React.createElement(OutlinedInput, { id: props.id, label: elementLabel, fullWidth: true, size: 'medium', type: 'text', required: (_f = props.config.validate) === null || _f === void 0 ? void 0 : _f.required, error: !!error, value: '' + value, disabled: props.config.disabled, readOnly: readOnly, onChange: (event) => {
                    if (readOnly) {
                        return;
                    }
                    props.internalData.value = event.target.value;
                    const number = parseFloat(event.target.value);
                    props.setDataNumberOrUndefined(Number.isFinite(number) ? number : undefined);
                    renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
                }, inputComponent: fieldIsInteger ? numberIntegerInputComponent : numberFloatInputComponent, inputProps: Object.assign(Object.assign({}, (props.editMode ? { tabIndex: -1 } : { inputMode: props.config.integer ? 'numeric' : 'decimal' })), { 'aria-describedby': ariaDescribedBy }), autoFocus: (_g = (!props.editMode && props.config.autoFocus)) !== null && _g !== void 0 ? _g : false })),
        React.createElement(FormHelperErrorDescription, { descriptionId: ariaDescribedBy, error: error, description: props.config.description })));
};
const WidgetFormNumberEditor = (props) => {
    const { t } = useCommonFormUiTranslation();
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, { title: t('widget.widgetInput.number') + ': ' + props.config.label }, buildGeneralTab(props, {
            key: true,
            label: true,
            appendRowIndexToLabel: true,
            readOnly: true,
            description: true,
            tooltip: true,
            autoFocus: true,
            children: [
                {
                    type: 'form.checkbox',
                    key: 'integer',
                    label: t('widget.widgetInput.isWholeNumber'),
                },
                {
                    type: 'form.number',
                    key: 'numberOfFractionalDigits',
                    label: t('widget.widgetInput.numberOfFractions'),
                    hidden: { when: '^integer', dataEq: true },
                },
            ],
        }), buildValidateTab(props, { required: true, uniqueInList: true, min: true, max: true }), buildHiddenTab(props, { t, allowDisable: true }), buildStyleTab(props), buildEventTab(props, { eventOnChange: true, autoSave: true })) }),
        React.createElement(WidgetFormNumber, Object.assign({}, props))));
};
export const widgetFormNumber = {
    type,
    validate: (props) => {
        var _a, _b, _c, _d;
        if (isSkipValidate(props)) {
            return;
        }
        const value = props.getDataNumberOrUndefined();
        if (props.config.validate) {
            if (props.config.validate.required) {
                if (value == null) {
                    return (_a = props.config.validate.requiredMessage) !== null && _a !== void 0 ? _a : (props.config.label ? props.config.label + ' is required' : 'Required');
                }
            }
            if (props.config.validate.min != null) {
                if (value != null && value < props.config.validate.min) {
                    return (_b = props.config.validate.minMessage) !== null && _b !== void 0 ? _b : props.config.label + ' cannot be less than ' + props.config.validate.min;
                }
            }
            if (props.config.validate.max != null) {
                if (value != null && value > props.config.validate.max) {
                    return (_c = props.config.validate.maxMessage) !== null && _c !== void 0 ? _c : props.config.label + ' cannot be greater than ' + props.config.validate.max;
                }
            }
            if (props.config.validate.uniqueInList || props.config.validate.uniqueInGrid) {
                const error = validateUniqueInList(props, value, props.dataPath);
                if (error) {
                    return (_d = props.config.validate.uniqueInListMessage) !== null && _d !== void 0 ? _d : error;
                }
            }
        }
        return validateCustom(props, value);
    },
    update: handleWidgetUpdate,
    getLabelValueAsTextPairs: (props, labelPrefix) => {
        return getLabelAndValuePairs(joinLabels(labelPrefix, props.config.label), props.getDataNumberOrUndefined());
    },
    component: WidgetFormNumber,
    icon: React.createElement(NumbersIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormNumberEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, { type, key: 'number', label: 'Number' }),
};
